import React, { useEffect, useMemo, useState } from 'react';
import { Linking, Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { WebView } from 'react-native-webview';

import { Palette, spacing, useThemePalette } from '../theme';
import { track, recordError } from '../lib/analytics';
import { ApplyField } from '../lib/apply/applyFields';
import ApplyKitChips from './ApplyKitChips';

interface ApplyBrowserProps {
  visible: boolean;
  onClose: () => void;
  url: string;
  position?: string;
  company?: string;
  // Resume contact fields for the collapsible Apply Kit panel.
  fields: ApplyField[];
}

// Full-screen in-app browser for a job posting. Lets the user read/apply
// without leaving the app, with a one-tap Apply Kit panel for pasting their
// resume contacts. Intentionally does NOT mark the job as applied — opening is
// not applying (people open just to read). The header keeps an "open in
// external browser" button for sites that misbehave inside the WebView.
const ApplyBrowser: React.FC<ApplyBrowserProps> = ({
  visible,
  onClose,
  url,
  position,
  company,
  fields,
}) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [kitOpen, setKitOpen] = useState(false);

  // The modal stays mounted across openings (visible is toggled), so collapse
  // the Apply Kit panel when it closes — each job opens with it tucked away.
  useEffect(() => {
    if (!visible) setKitOpen(false);
  }, [visible]);

  const openExternal = () => {
    track('apply_open_external');
    Linking.openURL(url).catch((err) => recordError(err, `apply_open_external`));
  };

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={onClose}
            hitSlop={8}
            style={styles.headerBtn}
            accessibilityLabel={t('common.cancel')}
          >
            <MaterialCommunityIcons name="close" size={24} color={palette.textPrimary} />
          </TouchableOpacity>
          <View style={styles.headerTitleWrap}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {position || t('applyBrowser.title')}
            </Text>
            {company ? (
              <Text style={styles.headerCompany} numberOfLines={1}>
                {company}
              </Text>
            ) : null}
          </View>
          <TouchableOpacity
            onPress={openExternal}
            hitSlop={8}
            style={styles.headerBtn}
            accessibilityLabel={t('applyKit.openExternal')}
          >
            <MaterialCommunityIcons name="open-in-new" size={22} color={palette.textPrimary} />
          </TouchableOpacity>
        </View>

        <WebView
          source={{ uri: url }}
          style={styles.webview}
          startInLoadingState
          allowsBackForwardNavigationGestures
        />

        {fields.length > 0 && (
          <View style={styles.kitPanel}>
            <TouchableOpacity
              style={styles.kitToggle}
              onPress={() => setKitOpen((o) => !o)}
              activeOpacity={0.8}
            >
              <MaterialCommunityIcons name="form-textbox" size={16} color={palette.primary} />
              <Text style={styles.kitToggleText}>{t('applyKit.title')}</Text>
              <MaterialCommunityIcons
                name={kitOpen ? 'chevron-down' : 'chevron-up'}
                size={20}
                color={palette.textMuted}
              />
            </TouchableOpacity>
            {kitOpen && (
              <View style={styles.kitChipsWrap}>
                <ApplyKitChips fields={fields} showHeader={false} />
              </View>
            )}
          </View>
        )}
      </SafeAreaView>
    </Modal>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safe: { flex: 1, backgroundColor: p.canvas },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
      backgroundColor: p.surface,
    },
    headerBtn: { padding: spacing.xs },
    headerTitleWrap: { flex: 1 },
    headerTitle: { fontSize: 15, fontWeight: '700', color: p.textPrimary },
    headerCompany: { fontSize: 12, color: p.textSecondary },
    webview: { flex: 1, backgroundColor: p.canvas },
    kitPanel: {
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
      backgroundColor: p.surface,
      paddingHorizontal: spacing.md,
      paddingBottom: spacing.xs,
    },
    kitToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
    },
    kitToggleText: { flex: 1, fontSize: 14, fontWeight: '700', color: p.textPrimary },
    kitChipsWrap: { paddingBottom: spacing.sm },
  });

export default ApplyBrowser;
